/**
 * Guardie di rotta e collegamento delle pagine di accesso allo stato della
 * sessione.
 *
 * Lo stato della sessione non si deduce nel browser: lo riferisce il gateway
 * (`anonymous`, `pending_enrollment`, `pending_mfa`, `active`). Prima era una
 * catena di `if` che sceglieva quale pagina montare; con un router diventa un
 * indirizzo, e la regola che lo calcola è una funzione pura in `session.ts`.
 *
 * Il contratto con il gateway non cambia: stesse rotte, stessi campi, stesso
 * cookie. Cambia solo dove l'utente si trova mentre le attraversa.
 *
 * Tutte le pagine di questo file sono in chunk separati, **compresa quella di
 * accesso**. La pagina di accesso però viene richiesta subito, senza aspettare
 * che React la renda: la sua `import()` parte alla valutazione del modulo, cioè
 * in parallelo alla domanda «che sessione ho?» che l'applicazione rivolge al
 * gateway. Il giro di rete c'è comunque, e il codice arriva mentre si aspetta
 * la risposta — così l'utente non attende un round trip in più e il chunk
 * iniziale non porta il motore dei form a chi ha già una sessione valida.
 */

import { lazy, useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { PASSWORD_ONLY, type LoginMethods } from '../api/account';
import { useApi } from '../api/ApiContext';
import { useAuth } from '../auth/AuthContext';
import { paths } from './routes';
import { requiredPath } from './session';

// La richiesta parte qui, non al primo render: vedi il commento in testa.
const loginModule = import('../pages/auth/LoginPage');
const LoginPage = lazy(async () => ({ default: (await loginModule).LoginPage }));

const MfaEnrollPage = lazy(async () => ({
  default: (await import('../pages/auth/MfaEnrollPage')).MfaEnrollPage,
}));
const MfaVerifyPage = lazy(async () => ({
  default: (await import('../pages/auth/MfaVerifyPage')).MfaVerifyPage,
}));
const RecoveryCodesPage = lazy(async () => ({
  default: (await import('../pages/auth/RecoveryCodesPage')).RecoveryCodesPage,
}));

/**
 * Legge dallo stato di autenticazione il percorso a cui la sessione obbliga.
 *
 * @returns Il percorso obbligato, oppure `null` se l'area autenticata è
 *   raggiungibile.
 */
function useRequiredPath(): string | null {
  const { status, recoveryCodes } = useAuth();
  return requiredPath({ status, hasPendingRecoveryCodes: recoveryCodes !== null });
}

/**
 * Lascia passare solo una sessione attiva.
 *
 * @returns Le rotte figlie, oppure un rimbalzo verso il passo mancante.
 */
export function RequireSession(): JSX.Element {
  const required = useRequiredPath();
  const location = useLocation();

  if (required !== null) {
    // Il percorso richiesto viaggia nello stato: dopo l'accesso si torna dove
    // si stava andando, invece di finire sempre sull'elenco dei progetti.
    return <Navigate to={required} replace state={{ from: location.pathname + location.search }} />;
  }
  return <Outlet />;
}

/** Proprietà della guardia di una tappa dell'accesso. */
export interface RequireStageProps {
  /** Percorso della tappa protetta. */
  stage: string;
}

/**
 * Lascia passare solo chi si trova davvero a questa tappa dell'accesso.
 *
 * Impedisce, per esempio, di aprire la verifica in due passaggi con una
 * sessione già attiva, o la registrazione quando il secondo fattore c'è già.
 *
 * @param props.stage Percorso della tappa protetta.
 * @returns Le rotte figlie, oppure un rimbalzo verso la tappa giusta.
 */
export function RequireStage({ stage }: RequireStageProps): JSX.Element {
  const required = useRequiredPath();
  const { status, mfaEnrolled, impersonation } = useAuth();

  if (required === stage) {
    return <Outlet />;
  }
  // Registrazione volontaria: per chi non è obbligato la verifica è facoltativa
  // (D14) e la si attiva dal profilo con la sessione già attiva.
  if (
    stage === paths.mfaEnroll &&
    required === null &&
    status === 'active' &&
    !mfaEnrolled &&
    impersonation === null
  ) {
    return <Outlet />;
  }
  return <Navigate to={required ?? paths.root} replace />;
}

/**
 * Pagina di accesso collegata allo stato di autenticazione.
 *
 * @returns L'elemento della pagina.
 */
export function LoginRoute(): JSX.Element {
  const { login, error, busy } = useAuth();
  const { account } = useApi();
  const location = useLocation();
  const [methods, setMethods] = useState<LoginMethods>(PASSWORD_ONLY);

  // Modi di accesso (ADR 0033). Se il gateway non risponde resta la password, come prima.
  useEffect(() => {
    let alive = true;
    account
      .loginMethods()
      .then((loaded) => {
        if (alive) {
          setMethods(loaded);
        }
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [account]);

  const googleError = new URLSearchParams(location.search).get('google');
  const from = (location.state as { from?: unknown } | null)?.from;
  return (
    <LoginPage
      onSubmit={login}
      error={error}
      busy={busy}
      methods={methods}
      googleError={googleError}
      nextPath={typeof from === 'string' ? from : undefined}
    />
  );
}

/**
 * Registrazione della verifica in due passaggi.
 *
 * @returns L'elemento della pagina.
 */
export function MfaEnrollRoute(): JSX.Element {
  const { enrollment, enrollMfa, verifyEnrollment, error, busy, status } = useAuth();
  const navigate = useNavigate();
  // Solo la registrazione volontaria si può lasciare: quella obbligatoria è
  // l'unica cosa che una sessione `pending_enrollment` può fare.
  const voluntary = status === 'active';
  return (
    <MfaEnrollPage
      enrollment={enrollment}
      onStart={enrollMfa}
      onVerify={verifyEnrollment}
      onCancel={voluntary ? () => navigate(paths.profile) : undefined}
      error={error}
      busy={busy}
    />
  );
}

/**
 * Verifica in due passaggi.
 *
 * @returns L'elemento della pagina.
 */
export function MfaVerifyRoute(): JSX.Element {
  const { verifyMfa, error, busy, logout } = useAuth();
  return <MfaVerifyPage onVerify={verifyMfa} error={error} busy={busy} onCancel={logout} />;
}

/**
 * Codici di recupero, mostrati una sola volta.
 *
 * @returns L'elemento della pagina.
 */
export function RecoveryCodesRoute(): JSX.Element {
  const { recoveryCodes, acknowledgeRecoveryCodes } = useAuth();
  return <RecoveryCodesPage codes={recoveryCodes ?? []} onAcknowledge={acknowledgeRecoveryCodes} />;
}
